import type { MantineTheme } from '@mantine/core';
import type { CSSProperties } from 'react';
import { APP_BRAND } from './brand';
import { getDhtmlxGanttCssVariables } from './dhtmlxGanttTheme';

/**
 * All CSS variables for the Gantt page chart container (custom + dhtmlx overrides).
 */
export function getGanttChartCssVariables(
  theme: MantineTheme,
  colorScheme: 'light' | 'dark'
): CSSProperties {
  const isDark = colorScheme === 'dark';
  const g = APP_BRAND.gantt;

  const customVars: CSSProperties = {
    '--gantt-location-bg': g.location.bg,
    '--gantt-location-border': g.location.border,
    '--gantt-activity-bg': g.activity.bg,
    '--gantt-activity-border': g.activity.border,
    '--gantt-completed-bg': g.completed.bg,
    '--gantt-completed-border': g.completed.border,
    '--gantt-critical-border': g.critical.border,
    '--gantt-critical-shadow': isDark ? 'rgba(250, 82, 82, 0.45)' : g.critical.shadow,
    '--gantt-link-fs': g.links.finishStart,
    '--gantt-link-ss': g.links.startStart,
    '--gantt-link-ff': g.links.finishFinish,
    '--gantt-task-text': isDark ? theme.colors.gray[0] : theme.colors.dark[9],
    '--gantt-radius': theme.radius.sm,
    '--gantt-grid-bg': isDark ? theme.colors.dark[6] : theme.colors.gray[0],
    '--gantt-grid-border': isDark ? theme.colors.dark[4] : theme.colors.gray[3],
    '--gantt-grid-text': isDark ? theme.colors.gray[0] : theme.colors.dark[7],
    '--gantt-row-hover': isDark ? theme.colors.dark[5] : theme.colors.gray[0],
    '--gantt-row-selected': theme.colors.brand[isDark ? 8 : 0],
    '--gantt-cell-border': isDark ? theme.colors.dark[4] : theme.colors.gray[2],
    '--gantt-location-active-bg': isDark ? theme.colors.brand[9] : '#d0e3ff',
    '--gantt-location-readonly-bg': isDark ? theme.colors.dark[5] : '#e9ecef',
    '--gantt-location-readonly-text': isDark ? theme.colors.gray[5] : '#868e96',
    /** Soft non-leaf / schedule-readonly row tone — slightly darker than body for hierarchy. */
    '--gantt-schedule-readonly-bg': isDark ? theme.colors.dark[4] : '#f1f3f5',
    '--gantt-schedule-readonly-text': isDark ? theme.colors.gray[5] : '#ced4da',
    /** Faint tint for Start Δ / Finish Δ / Dur Δ headers in compare mode */
    '--gantt-baseline-delta-header-bg': isDark
      ? 'rgba(0, 180, 255, 0.16)'
      : 'rgba(0, 180, 255, 0.12)',
  };

  return {
    ...customVars,
    ...getDhtmlxGanttCssVariables(theme, colorScheme),
  };
}
